import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import { auditTypeQuery, type AuditArea, type AuditAreaPlan, type AuditRun, type AuditSide, type AuditTypePage as TypePage } from '../../api/audit'
import { type AskHave, type AskQuestion } from '../../api/auditAnswers'
import { moduleOf, shellQuery } from '../../api/shell'
import { Forbidden, isForbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'
import { AskQuestionRow } from './AskQuestionRow'
import { AreaSpec, SidePills } from './AuditGrid'
import { AUDIT_STYLES } from './AuditIndexPage'
import { AuditStrip } from './AuditStrip'
import { NextHandOut, RunActions, ScopeButton, ScopePanel, stepStatesOf, useRun } from './run/RunPanel'

/**
 * Un tip de audit, adanc: planul, butonul, fotografiile - macheta
 * `views/version-3/settings/audit/type.php`, element cu element.
 *
 *   1. Planul - ariile ca pasi, fiecare spunand ce citeste Adam din CMDB, ce
 *      ruleaza pe masina si ce trebuie sa intrebe un om. Se vede INAINTE de
 *      orice rulare: a sti ce se verifica e jumatate din ce cumperi cu un audit.
 *   2. Rularea - tinta si adancimea, un buton (`run/RunPanel`).
 *   3. Fotografiile - o foaie pe rulare, inghetata cu data ei.
 *
 * Nimic de aici nu inventeaza o stare: fara tabelul de rulari, butonul spune
 * ce pas lipseste si nu face nimic altceva.
 */

const STATUS_PILL: Record<string, string> = {
  queued: 'adm-pill-warn',
  running: 'adm-pill-running',
  done: 'adm-pill-ok',
  failed: 'adm-pill-bad',
  stopped: 'adm-pill-warn',
}

function PageHead({
  page,
  actions,
  titleExtra,
}: {
  page: TypePage
  actions: React.ReactNode
  titleExtra?: React.ReactNode
}) {
  /* Cate rulari sunt CU TOTUL, nu cate incap in lista taiata la zece. */
  const runsN = page.runsAll
  const rated = page.rated
  const stats = [
    { value: String(page.type.areas.length), label: 'areas' },
    { value: String(page.measurable), label: 'measurable here' },
    { value: String(page.tally.pass + page.tally.fail), label: 'decided by the portal' },
    { value: String(runsN), label: runsN === 1 ? 'run' : 'runs' },
    {
      value: rated.mean !== null ? `${rated.mean.toFixed(1)} / ${rated.lowest}` : '—',
      /* Si din ce e adunata: o medie care cuprinde arii notate in luni
         diferite arata ca o singura evaluare daca nu se spune. */
      label:
        rated.mean === null
          ? 'not rated yet'
          : rated.runs > 1
            ? `rated / lowest, from ${rated.runs} runs`
            : 'rated / lowest',
    },
  ]
  return (
    <div className="adm-page-head adm-tone-blue-mauve">
      <span className="adm-page-icon">
        <i className="fas fa-clipboard-check"></i>
      </span>
      <div className="adm-page-title">
        <div className="adm-page-title-line">
          <h3>{page.type.title}</h3>
          {titleExtra}
        </div>
        <span className="adm-page-note">{page.type.objective}</span>
      </div>
      <div className="adm-page-stats">
        {stats.map((stat) => (
          <div className="adm-stat adm-stat-blue-mauve" key={stat.label}>
            <span className="adm-stat-value">{stat.value}</span>
            <span className="adm-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
      <div className="adm-page-actions">{actions}</div>
    </div>
  )
}

/** Un pas al planului: o arie, cu cele doua jumatati ale ei sub un chevron. */
function StepCard({
  area,
  i,
  plan,
  sides,
  stepState,
  corner,
  questions,
  have,
  coverage,
  answersReady,
}: {
  area: AuditArea
  i: number
  plan: AuditAreaPlan
  sides: Record<string, AuditSide>
  stepState: string
  corner: React.ReactNode
  questions: AskQuestion[]
  have: Record<string, AskHave>
  coverage: { asked: number; answered: number; stale: number }
  answersReady: boolean
}) {
  const [info, setInfo] = useState(false)
  const [done, setDone] = useState(coverage.answered)
  const spec = 'auditSpec' + area.code.replace(/\./g, '_')
  const reach = plan.reach
  const cls =
    'adm-card od-audit-step' +
    (plan.measurable ? '' : ' is-ask-only') +
    (stepState === 'done' ? ' is-done' : '') +
    (stepState === 'running' ? ' is-running' : '')
  return (
    <article className={cls} data-code={area.code}>
      <div className="od-audit-corner">
        {corner}
        {/*
          UN SINGUR CHEVRON PE ARIE. Inauntru sunt doua jumatati: ce se face
          fara om, si ce se intreaba de la client. Erau in doua locuri diferite
          - partea automata aici sub „Info", intrebarile pe alt ecran - si
          nimic de pe cardul asta nu spunea daca au fost completate.
        */}
        <button
          type="button"
          className="od-audit-info"
          aria-expanded={info}
          aria-controls={spec}
          title="what runs without a person, and what only the client can answer"
          onClick={() => setInfo(!info)}
        >
          {coverage.asked > 0 && (
            <span
              className={`od-audit-answered${done >= coverage.asked ? ' is-done' : ''}`}
              title="answers the client has given for this area"
            >
              {done}/{coverage.asked} asked
            </span>
          )}
          <i className="fas fa-chevron-down" aria-hidden="true"></i> Details
        </button>
      </div>
      <div className="od-audit-step-head">
        <span className="od-audit-step-n" data-area={area.code}>
          {stepState === 'done' ? (
            <i className="fas fa-check" aria-hidden="true"></i>
          ) : stepState === 'running' ? (
            <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
          ) : (
            i + 1
          )}
        </span>
        <div>
          <h5>{area.title}</h5>
          <div className="od-audit-step-meta">
            <span className={`adm-pill od-audit-mode od-audit-mode-${area.mode.toLowerCase()}`}>{area.mode}</span>
            <span className="od-audit-auto-n" title="share of this area's evidence that runs without human judgement">
              {area.auto}% auto
            </span>
            {reach.checks > 0 && (
              <span
                className={`od-audit-reach od-audit-reach-${reach.level.replace(/ /g, '-')}`}
                title={`the manual judges this area by ${reach.checks} checks; the portal decides ${reach.decides} of them by itself`}
              >
                {reach.decides}/{reach.checks} decided here
              </span>
            )}
            <SidePills area={area} sides={sides} />
          </div>
          <p className="od-audit-step-scope">{area.scope}</p>
        </div>
      </div>
      {/*
        Doua jumatati, sub acelasi chevron. Stanga: ce se face FARA om - Anexa A
        a planului, uneltele din CMDB si comenzile de pe masina. Dreapta: ce nu
        poate raspunde niciun instrument, si se raspunde CHIAR AICI.
      */}
      <div className="od-audit-spec">
        <div className="od-audit-spec-body" id={spec} hidden={!info}>
          <div className="od-audit-halves">
            <section className="od-audit-half od-audit-half-auto">
              <h6 className="od-audit-half-head">
                <i className="fas fa-bolt" aria-hidden="true"></i> Automated
                <span className="adm-muted">what runs without a person</span>
              </h6>
              <AreaSpec area={area} between={<StepLanes plan={plan} />} auditor={false} />
            </section>

            <section className="od-audit-half od-audit-half-manual od-ask-area" data-area={area.code}>
              <h6 className="od-audit-half-head">
                <i className="fas fa-user" aria-hidden="true"></i> Manual &mdash; ask the client
                {coverage.asked > 0 && (
                  <span className="od-ask-count">
                    <span className="od-ask-done">{done}</span> of {coverage.asked} answered
                  </span>
                )}
              </h6>
              {!answersReady ? (
                <p className="adm-muted">
                  The answers table is not on this instance yet &mdash; Change Log &rsaquo; Re-apply schema
                  creates <code>it_audit_answers</code>.
                </p>
              ) : questions.length === 0 ? (
                <p className="adm-muted">Nothing &mdash; the data answers this one.</p>
              ) : (
                questions.map((q) => (
                  <AskQuestionRow
                    key={q.key}
                    area={area.code}
                    q={q}
                    have={have[q.key] ?? null}
                    onCoverage={setDone}
                  />
                ))
              )}
              {area.auditor !== '' && (
                <div className="od-audit-spec-part od-audit-spec-human">
                  <span className="od-audit-step-label">
                    <i className="fas fa-user-tie" aria-hidden="true"></i> Stays with the auditor
                  </span>
                  <p>{area.auditor}</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </article>
  )
}

/*
 * Doua coloane, nu trei. „Ask a person" era a treia, si era doar o LISTA a
 * intrebarilor; acum intrebarile stau in jumatatea manuala, cu raspunsurile
 * lor. Lasata aici, ar fi spus acelasi lucru de doua ori pe acelasi card, o
 * data fara sa se poata raspunde.
 *
 * Comentariul sta AICI, nu inauntrul lui `return (`: acolo `{/* ... *\/}` nu e
 * un copil al vreunui element, si `return` primeste doua expresii in loc de
 * una. Asta a fost eroarea care a oprit build-ul la 26.9.15.10.
 */
function StepLanes({ plan }: { plan: AuditAreaPlan }) {
  return (
      <div className="od-audit-step-body od-audit-step-body-2">
        <div className="od-audit-step-col">
          <span className="od-audit-step-label">
            <i className="fas fa-database" aria-hidden="true"></i> From CMDB
          </span>
          {plan.tools.length > 0 ? (
            <div className="od-audit-tools">
              {plan.tools.map((tool) => (
                <span key={tool} className="adm-pill od-audit-tool">
                  {tool}
                </span>
              ))}
            </div>
          ) : (
            <span className="adm-pill adm-pill-warn">the portal holds nothing for this</span>
          )}
        </div>
        <div className="od-audit-step-col">
          <span className="od-audit-step-label">
            <i className="fas fa-terminal" aria-hidden="true"></i> Read live from the machine
          </span>
          {plan.probes.length > 0 ? (
            <ul className="od-audit-live">
              {plan.probes.map((probe, k) => (
                <li key={k}>
                  {probe.why}
                  {probe.linux !== '' && (
                    <span className="od-audit-cmd" title="Linux">
                      <i className="fab fa-linux" aria-hidden="true"></i> {probe.linux}
                    </span>
                  )}
                  {probe.windows !== '' && (
                    <span className="od-audit-cmd" title="Windows">
                      <i className="fab fa-windows" aria-hidden="true"></i> {probe.windows}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : plan.live.length > 0 ? (
            <ul className="od-audit-live">
              {plan.live.map((l, k) => (
                <li key={k}>
                  {l.check}
                  <span className="od-audit-cmd">{l.command}</span>
                  <span className="adm-muted">for a person to run</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="adm-muted">nothing runs on a machine for this one</span>
          )}
        </div>
      </div>
  )
}

function Snapshots({ runs, all }: { runs: AuditRun[]; all: number }) {
  return (
    <>
      <div className="adm-section adm-section-grey" id="snapshots">
        <h4>
          <i className="fas fa-camera" aria-hidden="true"></i> Snapshots
        </h4>
        <span className="adm-section-note">
          one sheet per run, frozen with its date - open two and compare them
          {all > runs.length && ` · showing the newest ${runs.length} of ${all}`}
        </span>
      </div>
      <div className="adm-card od-audit-runs">
        {runs.length === 0 ? (
          <p className="adm-muted">None yet. A run writes one.</p>
        ) : (
          <table className="table od-audit-table">
            <thead>
              <tr>
                <th>Taken</th>
                <th>Company</th>
                <th>Who</th>
                <th>Analysis</th>
                <th>Report</th>
                <th>Solutions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((snap) => (
                <tr key={snap.ID}>
                  <td className="adm-nowrap">{snap.created_at}</td>
                  <td>{snap.company}</td>
                  <td>{snap.who.trim() !== '' ? snap.who : '—'}</td>
                  <td>
                    <span className={`adm-pill ${STATUS_PILL[snap.status] ?? ''}`}>{snap.status}</span>
                  </td>
                  <td>{snap.hasReport ? <i className="fas fa-check od-audit-yes" aria-hidden="true"></i> : <span className="adm-muted">&mdash;</span>}</td>
                  <td>{snap.hasSolutions ? <i className="fas fa-check od-audit-yes" aria-hidden="true"></i> : <span className="adm-muted">&mdash;</span>}</td>
                  <td className="adm-nowrap">
                    <a className="btn btn-link btn-sm" href={`/app/settings/audit/snapshot/${snap.ID}`}>
                      open the sheet
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export function AuditTypePage({ id }: { id: number }) {
  const location = useLocation()
  const shell = useQuery(shellQuery(moduleOf(location.pathname)))
  const page = useQuery(auditTypeQuery(id))
  usePageStyles(AUDIT_STYLES, shell.data?.version ?? '')

  if (isForbidden(page.error)) return <Forbidden />
  if (page.error) {
    return (
      <Content>
        <div className="adm-empty-inline">There is no audit type with that id.</div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  return <TypeScreen data={page.data} />
}

function TypeScreen({ data }: { data: TypePage }) {
  const run = useRun(data)
  const latest = data.latest
  const states = stepStatesOf(run.watch)
  const canRunOne = data.ready && data.mayRun && !(run.watch && run.watch.status === 'running')

  return (
    <Content>
      <div
        className="od-audit od-audit-deep"
        data-type={data.type.id}
        data-latest={run.watch ? run.watch.runId : latest ? latest.ID : 0}
        data-status={run.watch ? run.watch.status : latest ? latest.status : ''}
      >
        <AuditStrip types={data.types} current={data.type.id} runCounts={data.runCounts} />

        <PageHead
          page={data}
          titleExtra={
            <ScopeButton
              page={data}
              open={run.scopeOpen}
              says={run.says}
              onToggle={() => run.setScopeOpen(!run.scopeOpen)}
            />
          }
          actions={
            <>
              {/* Cu ce cuvinte ruleaza, la un click de aici: briefingul
                  hotaraste ce va scrie in raportul pornit de pe ecranul asta,
                  si se putea ajunge la el numai din meniul de setari. */}
              <a
                className="btn btn-default"
                href={`/app/settings/audit/prompts/${data.type.id}`}
                title="the words the agent is given for this audit"
              >
                <i className="fas fa-comment-dots" aria-hidden="true"></i> The briefing
              </a>
              <RunActions
                page={data}
                watch={run.watch}
                onStart={() => run.start('')}
                busy={run.busy}
              />
            </>
          }
        />

        {data.mayRun && data.ready && (
          <ScopePanel
            page={data}
            open={run.scopeOpen}
            depth={run.depth}
            onDepth={run.setDepth}
            chosen={run.chosen}
            onChosen={run.setChosen}
            rootRef={run.rootRef}
            count={run.count}
            onChange={run.recount}
            onClear={run.clear}
          />
        )}

        <NextHandOut watch={run.watch} />

        <div className="adm-section adm-section-blue-mauve">
          <h4>
            <i className="fas fa-tasks" aria-hidden="true"></i> What Adam will do, area by area
          </h4>
          <span className="adm-section-note">the tools are its own read-only ones; &quot;ask&quot; is what no tool can answer</span>
        </div>

        <div className="od-audit-steps">
          {data.type.areas.map((area, i) => (
            <StepCard
              key={area.code}
              area={area}
              i={i}
              plan={data.plan[area.code] ?? { tools: [], asks: [], measurable: false, live: [], probes: [], reach: { checks: 0, decides: 0, level: '' } }}
              sides={data.sides}
              stepState={states[area.code] ?? ''}
              questions={data.questions[area.code] ?? []}
              have={data.asked[area.code]?.have ?? {}}
              coverage={
                data.asked[area.code]?.coverage ?? {
                  asked: (data.questions[area.code] ?? []).length,
                  answered: 0,
                  stale: 0,
                }
              }
              answersReady={data.answersReady}
              corner={
                canRunOne ? (
                  <button
                    type="button"
                    className="od-audit-one"
                    data-area={area.code}
                    title="run this one area on its own, so you can check the answer"
                    disabled={run.busy}
                    onClick={() => run.start(area.code)}
                  >
                    <i className="fas fa-play" aria-hidden="true"></i> Run
                  </button>
                ) : null
              }
            />
          ))}
        </div>

        <Snapshots runs={data.runs} all={data.runsAll} />
      </div>
    </Content>
  )
}
